/**
 * Conversation Partners Service
 *
 * API functions for conversation partner CRUD operations.
 */

import { apiClient } from "./api";
import type {
  ConversationPartner,
  ConversationPartnerCreate,
  ConversationPartnerListResponse,
  ConversationPartnerUpdate,
  SetPrimaryPartnerResponse,
} from "../types/conversationPartner";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * List all conversation partners for the current user.
 * Partners are ordered by primary status (primary first), then by name.
 */
export async function listConversationPartners(): Promise<ConversationPartnerListResponse> {
  const response = await apiClient.get<ConversationPartnerListResponse>(
    `${API_PREFIX}/conversation-partners`
  );
  return response.data;
}

/**
 * Get a specific conversation partner by ID.
 */
export async function getConversationPartner(
  partnerId: string
): Promise<ConversationPartner> {
  const response = await apiClient.get<ConversationPartner>(
    `${API_PREFIX}/conversation-partners/${partnerId}`
  );
  return response.data;
}

/**
 * Create a new conversation partner.
 * If is_primary is true, any existing primary partner will be demoted.
 */
export async function createConversationPartner(
  data: ConversationPartnerCreate
): Promise<ConversationPartner> {
  const response = await apiClient.post<ConversationPartner>(
    `${API_PREFIX}/conversation-partners`,
    data
  );
  return response.data;
}

/**
 * Update an existing conversation partner.
 */
export async function updateConversationPartner(
  partnerId: string,
  data: ConversationPartnerUpdate
): Promise<ConversationPartner> {
  const response = await apiClient.put<ConversationPartner>(
    `${API_PREFIX}/conversation-partners/${partnerId}`,
    data
  );
  return response.data;
}

/**
 * Delete a conversation partner.
 * This will unlink any associated voice profile and reflections.
 */
export async function deleteConversationPartner(
  partnerId: string
): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/conversation-partners/${partnerId}`);
}

/**
 * Set a conversation partner as the primary partner.
 * Any existing primary partner will be demoted.
 */
export async function setPrimaryPartner(
  partnerId: string
): Promise<SetPrimaryPartnerResponse> {
  const response = await apiClient.post<SetPrimaryPartnerResponse>(
    `${API_PREFIX}/conversation-partners/${partnerId}/set-primary`
  );
  return response.data;
}
